import React, { useRef, useState } from 'react';
import {
  FiClock,
  FiPackage,
  FiMapPin,
  FiAlertCircle,
  FiMessageCircle,
  FiTruck,
  FiShoppingBag,
  FiUser,
  FiBell,
  FiImage,
  FiCreditCard,
  FiEdit2,
} from 'react-icons/fi';
import { useAuth } from 'shared/context/AuthContext';
import { fmtMonto } from 'shared/format';
import { faseDeEstado, colorDeFase } from './fases';
import s from './styles';

function isDark() {
  try {
    return document.documentElement.getAttribute('data-dark') === 'true';
  } catch {
    return false;
  }
}

const minutosDesde = (f) => {
  if (!f) return null;
  return Math.floor((Date.now() - new Date(f).getTime()) / 60000);
};

const fmtTiempoFase = (mins) => {
  if (mins === null || mins === undefined) return null;
  if (mins < 1) return 'Ahora';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h${m > 0 ? ` ${m}m` : ''}`;
};

const formatearFechaCompleta = (fecha) => {
  if (!fecha) return '';
  const d = new Date(fecha);
  return (
    d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' }) +
    ' ' +
    d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
  );
};

const PedidoCard = ({ pedido, activo, onClick, esImportador }) => {
  const { tieneVisibilidad } = useAuth();
  const v = (c) => tieneVisibilidad(c);

  const contarItems = () => {
    try {
      const ps = Array.isArray(pedido.productos)
        ? pedido.productos
        : typeof pedido.productos === 'string'
          ? JSON.parse(pedido.productos)
          : [];
      return ps.length;
    } catch {
      return 0;
    }
  };

  const esEntregado = pedido.estado === 'entregado' || pedido.estado === 'credito_pagado';
  const faseColor = colorDeFase(faseDeEstado(pedido.estado, esImportador));
  const sede = pedido.sede_nombre || pedido.codigo_sede || '';
  const esCorporativo = pedido.tipo_pedido === 'corporativo';
  const sucursal = pedido.sucursal_nombre || '';
  const minsEnFase = minutosDesde(pedido.fechaactualizacion);
  const tiempoUrgente = minsEnFase !== null && minsEnFase > 60;
  const tieneComprobante = !!pedido.comprobante_url;
  const enVerificacion = pedido.estado === 'pago_en_verificacion';
  const esRetiro = pedido.tipo_entrega === 'retiro';
  const esEncomienda = pedido.tipo_entrega === 'encomienda';

  return (
    <div
      className="pedido-card"
      onClick={(e) => {
        e.stopPropagation();
        onClick(pedido);
      }}
      style={{
        ...s.pedidoCard,
        borderLeftWidth: '3px',
        borderLeftStyle: 'solid',
        borderLeftColor: faseColor,
        boxShadow: activo ? '0 2px 16px rgba(26,115,232,0.30)' : '0 1px 3px rgba(0,0,0,0.10)',
      }}
    >
      <div style={s.pedidoCardTop}>
        <span style={s.pedidoCardId}>#{pedido.id}</span>
        {v('vis.pedidos.total') ? (
          <span style={s.pedidoCardTotal}>
            ${fmtMonto((typeof pedido.total === 'number' ? pedido.total : parseFloat(pedido.total || 0)) + (parseFloat(pedido.costo_envio) || 0))}
          </span>
        ) : null}
      </div>
      {pedido.noLeidos > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '3px',
              fontSize: '10px',
              fontWeight: 600,
              color: '#fff',
              backgroundColor: '#ea4335',
              padding: '2px 7px',
              borderRadius: '10px',
            }}
          >
            <FiMessageCircle size={10} /> {pedido.noLeidos}
          </span>
          <span style={{ fontSize: '10px', color: '#ea4335', fontWeight: 500 }}>sin leer</span>
        </div>
      )}
      {v('vis.pedidos.datos_cliente') ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={s.pedidoCardCliente}>{pedido.cliente || pedido.nombre || 'Sin nombre'}</span>
          {esCorporativo && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              fontSize: '9px',
              fontWeight: 600,
              color: '#7c3aed',
              backgroundColor: isDark() ? '#3b1f6e' : '#ede9fe',
              padding: '1px 6px',
              borderRadius: '8px',
              whiteSpace: 'nowrap',
            }}>
              <FiUser size={8} /> Corporativo
            </span>
          )}
          {pedido.solicitud_asesor && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              fontSize: '9px',
              fontWeight: 600,
              color: '#FF9500',
              backgroundColor: isDark() ? '#78350f' : '#fff7ed',
              padding: '1px 6px',
              borderRadius: '8px',
              whiteSpace: 'nowrap',
            }}>
              <FiBell size={8} /> Asesor
            </span>
          )}
        </div>
      ) : null}
      {sucursal && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
          <FiMapPin size={9} style={{ color: '#7c3aed' }} />
          <span style={{ fontSize: '10px', fontWeight: 500, color: 'var(--agx-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Sucursal: {sucursal}
          </span>
        </div>
      )}
      {pedido.atendido_por && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
          <FiUser size={9} style={{ color: 'var(--agx-green)' }} />
          <span style={{
            fontSize: '10px',
            fontWeight: 500,
            color: 'var(--agx-green)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {pedido.nombre_asesor || pedido.atendido_por}
          </span>
        </div>
      )}
      {sede && (
        <span style={s.pedidoCardSede}>
          <FiMapPin size={9} /> {sede}
        </span>
      )}
      <div style={s.pedidoCardMeta}>
        <span style={s.pedidoCardItems}>
          <FiPackage size={10} style={{ marginRight: '3px' }} />
          <span style={{ fontWeight: 600, marginRight: '3px' }}>{contarItems()}</span>
          items
          {(esRetiro || esEncomienda) && (
            <span
              style={{
                marginLeft: '4px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '2px',
                fontSize: '9px',
                padding: '1px 5px',
                borderRadius: '4px',
                background: esRetiro
                  ? isDark()
                    ? '#1e3a5f'
                    : '#dbeafe'
                  : isDark()
                    ? '#4a1942'
                    : '#fce7f3',
                color: esRetiro
                  ? isDark()
                    ? '#93c5fd'
                    : '#1e40af'
                  : isDark()
                    ? '#f9a8d4'
                    : '#9d174d',
                fontWeight: 600,
              }}
            >
              {esRetiro ? <FiShoppingBag size={8} /> : <FiTruck size={8} />}
              {esRetiro ? 'Retiro' : 'Encomienda'}
            </span>
          )}
        </span>
        <span style={s.pedidoCardTime}>
          <FiClock size={10} style={{ marginRight: '3px' }} />
          {formatearFechaCompleta(pedido.fechapedido)}
        </span>
      </div>
      {minsEnFase !== null && !esEntregado && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '10px',
            padding: '2px 6px',
            borderRadius: '6px',
            background: tiempoUrgente
              ? isDark()
                ? '#7f1d1d'
                : '#fef2f2'
              : minsEnFase > 30
                ? isDark()
                  ? '#78350f'
                  : '#fffbeb'
                : isDark()
                  ? '#064e3b'
                  : '#f0fdf4',
            color: tiempoUrgente
              ? isDark()
                ? '#fca5a5'
                : '#991b1b'
              : minsEnFase > 30
                ? isDark()
                  ? '#fcd34d'
                  : '#92400e'
                : isDark()
                  ? '#6ee7b7'
                  : '#166534',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            alignSelf: 'flex-start',
          }}
        >
          <FiClock size={9} />
          {fmtTiempoFase(minsEnFase)} en fase
        </div>
      )}
      {tieneComprobante && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '10px',
            padding: '3px 7px',
            borderRadius: '5px',
            background: enVerificacion
              ? isDark()
                ? '#2e1065'
                : '#ede9fe'
              : isDark()
                ? '#064e3b'
                : '#f0fdf4',
            color: enVerificacion
              ? isDark()
                ? '#c4b5fd'
                : '#6d28d9'
              : isDark()
                ? '#6ee7b7'
                : '#166534',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            alignSelf: 'flex-start',
          }}
        >
          {enVerificacion ? <FiCreditCard size={10} /> : <FiImage size={10} />}
          {enVerificacion ? 'Comprobante por verificar' : 'Comprobante de pago'}
        </div>
      )}
      {pedido.confirmacion_pendiente && (
        <span style={s.pedidoCardConfirmacionBadge}>
          <FiAlertCircle size={10} /> Esperando confirmación
        </span>
      )}
    </div>
  );
};

const PedidoFaseColumna = ({
  fase,
  index,
  pedidos,
  pedidoActivo,
  onSelectPedido,
  dragIndex,
  overIndex,
  onHeaderPointerDown,
  onRenameColumna,
  esImportador,
}) => {
  const [editando, setEditando] = useState(false);
  const [draft, setDraft] = useState(fase.label);

  const empezarEditar = () => {
    setDraft(fase.label);
    setEditando(true);
  };

  const guardar = () => {
    setEditando(false);
    onRenameColumna(fase.key, draft);
  };

  const cancelar = () => {
    setEditando(false);
    setDraft(fase.label);
  };

  const esOrigen = dragIndex === index;
  const esDestino = overIndex === index && dragIndex !== null && dragIndex !== index;

  return (
    <div
      data-kanban-key={fase.key}
      style={{
        ...s.faseColumna,
        opacity: esOrigen ? 0.55 : 1,
        boxShadow: esDestino ? '0 0 0 2px var(--agx-pri2)' : undefined,
        transition: 'box-shadow 0.12s ease, opacity 0.12s ease',
      }}
    >
      <div
        style={{
          ...s.faseHeader,
          backgroundColor: isDark() ? fase.color + '30' : fase.color + '15',
          borderBottomColor: fase.color,
          touchAction: 'none',
          cursor: 'grab',
          userSelect: 'none',
        }}
        onPointerDown={(e) => onHeaderPointerDown(e, index)}
        onMouseDown={(e) => e.stopPropagation()}
        onDoubleClick={empezarEditar}
        title="Arrastra para reordenar · doble clic para renombrar"
      >
        {editando ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') guardar();
              if (e.key === 'Escape') cancelar();
            }}
            onBlur={guardar}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: '13px',
              fontWeight: 600,
              padding: '4px 6px',
              border: '1px solid ' + fase.color,
              borderRadius: '6px',
              outline: 'none',
              background: 'var(--agx-surface)',
              color: 'var(--agx-text)',
              fontFamily: 'inherit',
            }}
          />
        ) : (
          <span style={{ ...s.faseTitle, color: fase.color }}>{fase.label}</span>
        )}
        <span
          style={{
            ...s.faseCount,
            backgroundColor: isDark() ? fase.color + '40' : fase.color + '25',
            color: isDark() ? '#e2e8f0' : fase.color,
          }}
        >
          {pedidos.length}
        </span>
        <button
          type="button"
          onClick={empezarEditar}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          aria-label="Renombrar columna"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '20px',
            height: '20px',
            padding: 0,
            border: 'none',
            borderRadius: '5px',
            background: 'transparent',
            color: 'var(--agx-faint)',
            cursor: 'pointer',
          }}
        >
          <FiEdit2 size={12} />
        </button>
      </div>
      <div className="fase-cards" style={s.faseCards}>
        {pedidos.map((p) => (
          <PedidoCard
            key={p.id}
            pedido={p}
            activo={pedidoActivo?.id === p.id}
            onClick={onSelectPedido}
            esImportador={esImportador}
          />
        ))}
        {pedidos.length === 0 && (
          <div
            style={{
              textAlign: 'center',
              color: 'var(--agx-faint)',
              fontSize: '12px',
              padding: '20px 8px',
            }}
          >
            Sin pedidos
          </div>
        )}
      </div>
    </div>
  );
};

const PedidosActivos = ({ pedidos, pedidoActivo, onSelectPedido, columnas, onRenameColumna, onMoverColumna, esImportador }) => {
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const dragRef = useRef(null);
  const overRef = useRef(null);

  const pedidosPorFase = {};
  columnas.forEach((f) => {
    pedidosPorFase[f.key] = [];
  });

  (pedidos || []).forEach((p) => {
    const fase = faseDeEstado(p.estado, esImportador);
    if (pedidosPorFase[fase]) {
      pedidosPorFase[fase].push(p);
    } else {
      pedidosPorFase['pendiente'].push(p);
    }
  });

  Object.keys(pedidosPorFase).forEach((fase) => {
    pedidosPorFase[fase].sort(
      (a, b) => new Date(a.fechapedido || 0) - new Date(b.fechapedido || 0)
    );
  });

  const handlePointerMove = (e) => {
    if (!dragRef.current) return;
    if (e.cancelable) e.preventDefault();
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const col = el && el.closest ? el.closest('[data-kanban-key]') : null;
    if (col) {
      const key = col.getAttribute('data-kanban-key');
      const idx = columnas.findIndex((c) => c.key === key);
      if (idx >= 0) {
        overRef.current = idx;
        setOverIndex(idx);
      }
    }
  };

  const handlePointerUp = () => {
    window.removeEventListener('pointermove', handlePointerMove);
    window.removeEventListener('pointerup', handlePointerUp);
    window.removeEventListener('pointercancel', handlePointerUp);
    if (dragRef.current) {
      const from = dragRef.current.index;
      const to = overRef.current;
      if (to !== null && to !== undefined && to !== from) {
        onMoverColumna(from, to);
      }
    }
    dragRef.current = null;
    overRef.current = null;
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleHeaderPointerDown = (e, index) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    if (e.cancelable) e.preventDefault();
    e.stopPropagation();
    dragRef.current = { index };
    overRef.current = index;
    setDragIndex(index);
    setOverIndex(index);
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
  };

  return (
    <>
      <style>{`
        .pedido-card {
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .pedido-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 20px rgba(0,0,0,0.10);
        }
        .pedido-card:active {
          transform: translateY(0);
        }
        .fase-cards::-webkit-scrollbar { width: 4px; }
        .fase-cards::-webkit-scrollbar-thumb { background: var(--agx-border); border-radius: 4px; }
      `}</style>
      {columnas.map((fase, index) => (
        <PedidoFaseColumna
          key={fase.key}
          fase={fase}
          index={index}
          pedidos={pedidosPorFase[fase.key] || []}
          pedidoActivo={pedidoActivo}
          onSelectPedido={onSelectPedido}
          dragIndex={dragIndex}
          overIndex={overIndex}
          onHeaderPointerDown={handleHeaderPointerDown}
          onRenameColumna={onRenameColumna}
          esImportador={esImportador}
        />
      ))}
    </>
  );
};

export default PedidosActivos;
